import React, { useState, useRef, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useQueryClient } from '@tanstack/react-query';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Copy, Trash2, Check, Edit, Unplug, X, ImageIcon } from 'lucide-react';
import {
  updateFixture,
  deleteFixture,
  cloneFixture,
  batchUpdateFixtureScale,
} from '@/services/fixtureService';
import { assignFixtureToCircuit } from '@/services/floorPlanService';
import { renameCircuit } from '@/services/circuitService';
import { toast } from 'sonner';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { ProductSelectionModal } from '@/features/product-library/components';
import { useProduct } from '@/features/product-library/hooks/useProductLibrary';
import { ResizeControl } from './shared/ResizeControl';
import { CategoryEditSection } from './shared/CategoryEditSection';
import { IconSelector, type IconData } from '@/components/shared/IconSelector';
import { organizationCategoryService } from '@/features/product-library/services/organizationCategoryService';
import { Separator } from '@/components/ui/separator';
import type { MarkerSizesConfig } from '@/types/floorPlanViewer';
import { useMergedCategoriesHierarchy } from '@/features/product-library/hooks/useMergedCategories';

export function FixtureDetailsDrawer() {
  // ✅ Zustand store state
  const activeDrawer = useAppStore(state => state.ui.activeDrawer);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const selectedFixtures = useAppStore(state => state.selection.fixtures);
  const clearEntitySelection = useAppStore(state => state.clearEntitySelection);
  const setHoveredEntity = useAppStore(state => state.setHoveredEntity);

  // ✅ FloorPlan context data
  const { data, floorPlan, incrementCategoryIconVersion } = useFloorPlanContext();
  const queryClient = useQueryClient();

  // ✅ Get baseScale from floor plan metadata for proper icon sizing
  const markerSizes = data.floorPlanMetadata?.marker_sizes as MarkerSizesConfig | null;
  const fixturesBaseScale = markerSizes?.fixtures_base_scale ?? 1.0;

  // ✅ Local component state
  const [isEditingCircuit, setIsEditingCircuit] = useState(false);
  const [newCircuitName, setNewCircuitName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [tempWattage, setTempWattage] = useState<string>('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [isSavingIcon, setIsSavingIcon] = useState(false);
  const wattageInputRef = useRef<HTMLInputElement>(null);
  const circuitNameInputRef = useRef<HTMLInputElement>(null);

  // ✅ Computed values
  const totalSelections = selectedFixtures.size + useAppStore.getState().selection.controls.size;
  const isOpen = activeDrawer === 'fixture' && selectedFixtures.size === 1 && totalSelections === 1;
  const selectedCount = selectedFixtures.size;
  const selectedFixtureId = selectedCount === 1 ? Array.from(selectedFixtures)[0] : null;
  const selectedFixture = selectedFixtureId
    ? data.fixtures?.find(f => f.id === selectedFixtureId)
    : null;

  // Get product info if fixture has a product_id
  const { data: linkedProduct } = useProduct(selectedFixture?.product_id || undefined);

  // Get merged categories (system + org) for lighting fixtures
  const { data: lightingTemplates = [] } = useMergedCategoriesHierarchy('lighting_fixtures');

  // Get control info
  const fixtureCircuit = selectedFixture?.circuit_id
    ? data.circuits?.find(c => c.id === selectedFixture.circuit_id)
    : null;

  // Get available circuits for assignment (excluding the currently assigned one)
  const availableCircuits =
    data.circuits
      ?.filter(c => c.id !== selectedFixture?.circuit_id)
      ?.sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0)) || [];

  // ✅ Event handlers
  const handleClose = () => {
    // Save any pending wattage changes before closing
    if (selectedFixture && tempWattage && tempWattage !== selectedFixture.wattage?.toString()) {
      const wattageValue = tempWattage ? Number(tempWattage) : null;
      handleUpdateWattage(wattageValue);
    }

    setActiveDrawer(null);
    clearEntitySelection('fixture');
    setHoveredEntity(null, null);
    setIsEditingCircuit(false);
    setNewCircuitName('');
    setTempWattage('');
    setShowProductModal(false);
  };

  const handleUpdateWattage = async (wattage: number | null) => {
    if (!selectedFixture) return;

    try {
      setIsUpdating(true);
      await updateFixture(selectedFixture.id, { wattage });
      await data.refetch();
      toast.success('Wattage updated');
    } catch (error) {
      logger.error('Error updating wattage:', error);
      toast.error('Failed to update wattage');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleWattageBlur = () => {
    const wattageValue = tempWattage ? Number(tempWattage) : null;
    if (selectedFixture && wattageValue !== selectedFixture.wattage) {
      handleUpdateWattage(wattageValue);
    }
  };

  const handleWattageKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation(); // Prevent space-to-pan interference
    if (e.key === 'Enter') {
      handleWattageBlur();
      wattageInputRef.current?.blur();
    }
  };

  const handleSheetInteraction = () => {
    // Save wattage if user clicks outside while editing
    if (wattageInputRef.current === document.activeElement) {
      handleWattageBlur();
    }
  };

  const handleAssignToCircuit = async (circuitId: string | null) => {
    if (!selectedFixture) return;

    // Handle "none" selection to unassign circuit
    const actualCircuitId = circuitId === 'none' ? null : circuitId;

    try {
      setIsUpdating(true);
      await assignFixtureToCircuit(selectedFixture.id, actualCircuitId);
      await data.refetch();
      setHoveredEntity(null, null);
      toast.success(actualCircuitId ? 'Load assigned' : 'Load removed');
    } catch (error) {
      logger.error('Error assigning load:', error);
      toast.error('Failed to assign load');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloneFixture = async () => {
    if (!selectedFixture) return;

    try {
      setIsUpdating(true);
      const newFixture = await cloneFixture(selectedFixture.id, data.fixtures || []);
      await data.refetch();

      // Select the new fixture
      clearEntitySelection('fixture');
      useAppStore.getState().selectEntity('fixture', newFixture.id);

      toast.success('Fixture cloned');
    } catch (error) {
      logger.error('Error cloning fixture:', error);
      toast.error('Failed to clone fixture');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteFixture = async () => {
    if (!selectedFixture) return;

    try {
      setIsUpdating(true);
      await deleteFixture(selectedFixture.id);
      await data.refetch();
      handleClose();
      toast.success('Fixture deleted');
    } catch (error) {
      logger.error('Error deleting fixture:', error);
      toast.error('Failed to delete fixture');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUnassignProduct = async () => {
    if (!selectedFixture) return;

    try {
      setIsUpdating(true);

      // Build update object
      const updateData: any = {
        product_id: null,
        // Clear wattage when unassigning product
        wattage: null,
      };

      // If unassigning a Portal product, clear Portal metadata
      if (selectedFixture.category_info?.from_portal) {
        updateData.category_info = {
          ...selectedFixture.category_info,
          portal_product_id: null,
          from_portal: false,
        };
      }

      // Update fixture with product_id and cleared Portal metadata (if unassigning)
      await updateFixture(selectedFixture.id, updateData);
      await data.refetch();

      // Clear temp wattage
      setTempWattage('');

      toast.success('Product unassigned');
    } catch (error) {
      logger.error('Error unassigning product:', error);
      toast.error('Failed to unassign product');
    } finally {
      setIsUpdating(false);
    }
  };

  const startEditCircuit = () => {
    if (!fixtureCircuit) return;
    setNewCircuitName(fixtureCircuit.name);
    setIsEditingCircuit(true);
  };

  const cancelEditCircuit = () => {
    setIsEditingCircuit(false);
    setNewCircuitName('');
  };

  const saveCircuitName = async () => {
    if (!fixtureCircuit || !newCircuitName.trim()) return;

    try {
      setIsUpdating(true);
      await renameCircuit(fixtureCircuit.id, newCircuitName.trim());
      await data.refetch();
      setIsEditingCircuit(false);
      setNewCircuitName('');
      toast.success('Load renamed');
    } catch (error) {
      logger.error('Error renaming load:', error);
      toast.error('Failed to rename load');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCircuitNameKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation(); // Prevent space-to-pan interference
    if (e.key === 'Enter') {
      saveCircuitName();
    } else if (e.key === 'Escape') {
      cancelEditCircuit();
    }
  };

  const handleFixtureResize = async (
    ids: string[],
    scale: number,
    saveAsDefault: boolean,
    scope: 'single' | 'category' | 'subcategory' | 'type',
  ) => {
    setIsUpdating(true);
    try {
      logger.info('Fixture resize:', {
        ids,
        scale,
        saveAsDefault,
        scope,
        category: selectedFixture?.category,
        subcategory: selectedFixture?.subcategory,
      });

      // Validate we have entities to resize
      if (ids.length === 0) {
        toast.error('No fixtures selected for resizing');
        return;
      }

      await batchUpdateFixtureScale(ids, scale);

      if (saveAsDefault && selectedFixture) {
        // Validate fixture has a category before saving default
        if (!selectedFixture.category) {
          logger.error('Cannot save default: fixture has no category');
          toast.error('Cannot save default size: fixture category is missing');
          return;
        }

        const subcategoryForDefault =
          scope === 'subcategory' ? selectedFixture.subcategory || undefined : undefined;
        logger.info('Saving default scale for fixture (per floor plan):', {
          category: selectedFixture.category,
          subcategory: subcategoryForDefault,
          floorPlanId: floorPlan.id,
        });

        // Save to floor plan metadata (per-floor-plan defaults)
        await data.saveCategoryDefault(
          'fixture',
          selectedFixture.category,
          scale,
          subcategoryForDefault,
        );

        logger.info('Default scale saved successfully to floor plan');

        // Build descriptive success message
        const categoryName = selectedFixture.category.replace(/_/g, ' ');
        const scopeName = subcategoryForDefault
          ? `${categoryName} > ${selectedFixture.subcategory?.replace(/_/g, ' ')}`
          : categoryName;
        toast.success(
          `Resized ${ids.length} fixture(s) and saved as default for ${scopeName} fixtures on this floor plan`,
        );
      } else {
        toast.success(`Resized ${ids.length} fixture(s)`);
      }

      await data.refetch();
    } catch (error) {
      logger.error('Failed to resize fixtures:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to resize fixtures';
      toast.error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  // ✅ Effects
  // Initialize temp wattage when fixture changes
  useEffect(() => {
    if (selectedFixture) {
      setTempWattage(selectedFixture.wattage?.toString() || '');
    }
  }, [selectedFixture?.id, selectedFixture?.wattage]);

  // Prevent auto-focus on any element when drawer opens
  useEffect(() => {
    if (isOpen) {
      // Use setTimeout to ensure this runs after any auto-focus behavior
      setTimeout(() => {
        // Remove focus from any active element
        if (document.activeElement && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }, 0);
    }
  }, [isOpen]);

  // Focus circuit name input when editing starts
  useEffect(() => {
    if (isEditingCircuit && circuitNameInputRef.current) {
      circuitNameInputRef.current.focus();
      circuitNameInputRef.current.select();
    }
  }, [isEditingCircuit]);

  // Clear hover when drawer closes
  useEffect(() => {
    return () => {
      if (!isOpen) {
        setHoveredEntity(null, null);
      }
    };
  }, [isOpen, setHoveredEntity]);

  if (!isOpen) return null;

  // ✅ Multi-selection view
  if (selectedCount > 1) {
    return (
      <Sheet open={isOpen} modal={false}>
        <SheetContent
          className="w-[400px] sm:w-[540px] [&>button]:hidden"
          side="right"
          onPointerDownOutside={handleSheetInteraction}
          onInteractOutside={handleSheetInteraction}
        >
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle>Multiple Fixtures Selected</SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="h-8 w-8"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Selection Summary */}
            <div className="text-center py-6 border rounded-lg">
              <div className="text-3xl font-bold text-blue-600 mb-2">{selectedCount}</div>
              <div className="text-gray-600">Fixtures Selected</div>
            </div>

            {/* Bulk Actions */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Bulk Actions</Label>
                <div className="grid gap-2">
                  <Button variant="outline" className="w-full" disabled={isUpdating}>
                    <Copy className="h-4 w-4 mr-2" />
                    Assign to Load
                  </Button>
                  <Button variant="outline" className="w-full" disabled={isUpdating}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Properties
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => clearEntitySelection('fixture')}
                    disabled={isUpdating}
                  >
                    <Unplug className="h-4 w-4 mr-2" />
                    Clear Selection
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // ✅ Single fixture view
  if (selectedFixture) {
    return (
      <>
        <Sheet open={isOpen} modal={false}>
          <SheetContent
            className="w-[400px] sm:w-[540px] flex flex-col h-full [&>button]:hidden"
            side="right"
            onPointerDownOutside={handleSheetInteraction}
            onInteractOutside={handleSheetInteraction}
          >
            <SheetHeader>
              <div className="flex items-center justify-between">
                <SheetTitle>Lighting Fixture</SheetTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                  className="h-8 w-8"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto py-4 space-y-6">
              {/* Fixture Details */}
              <div className="p-4 border rounded-lg space-y-4">
                <h4 className="text-sm font-medium">Fixture Details</h4>

                <div className="space-y-3">
                  {/* Type / Category */}
                  <CategoryEditSection
                    category={selectedFixture.category}
                    subcategory={selectedFixture.subcategory}
                    productType="lighting_fixtures"
                    onSave={async (category, subcategory) => {
                      await updateFixture(selectedFixture.id, {
                        category,
                        subcategory,
                        category_info: {
                          ...selectedFixture.category_info,
                          category,
                          subcategory,
                        },
                      });
                      await data.refetch();
                      toast.success('Fixture type updated');
                    }}
                    isUpdating={isUpdating}
                    label="Type"
                  />

                  {/* Wattage */}
                  <div className="flex justify-between items-center">
                    <Label htmlFor="wattage">Wattage</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="wattage"
                        ref={wattageInputRef}
                        type="number"
                        value={tempWattage}
                        onChange={e => setTempWattage(e.target.value)}
                        onBlur={handleWattageBlur}
                        onKeyDown={handleWattageKeyDown}
                        onKeyUp={e => e.stopPropagation()}
                        placeholder="Watts"
                        className="w-20 h-8 text-right"
                        disabled={isUpdating}
                        autoFocus={false}
                      />
                      <span className="text-sm text-gray-500">W</span>
                    </div>
                  </div>

                  {/* Room Assignment - Only show if room_id field exists */}
                  {'room_id' in selectedFixture && (
                    <div className="flex justify-between items-center">
                      <Label>Room</Label>
                      <Select
                        value={selectedFixture.room_id || 'none'}
                        onValueChange={async value => {
                          const roomId = value === 'none' ? null : value;
                          try {
                            setIsUpdating(true);

                            // Update fixture with room assignment
                            const supabase = await getAuthenticatedSupabaseClient();
                            const { error } = await supabase
                              .from('lighting_fixtures')
                              .update({ room_id: roomId })
                              .eq('id', selectedFixture.id);

                            if (error) throw error;

                            const roomName = roomId
                              ? data.rooms?.find(r => r.id === roomId)?.name
                              : null;
                            toast.success(
                              roomName ? `Assigned to ${roomName}` : 'Room assignment removed',
                            );
                            await data.refetch();
                          } catch (error) {
                            logger.error('Error assigning room:', error);
                            toast.error(
                              'Failed to assign room. The database may need to be updated.',
                            );
                          } finally {
                            setIsUpdating(false);
                          }
                        }}
                        disabled={isUpdating}
                      >
                        <SelectTrigger className="w-[180px] h-8">
                          <SelectValue placeholder="Select room" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No room</SelectItem>
                          {data.rooms?.map(room => (
                            <SelectItem key={room.id} value={room.id}>
                              {room.name || 'Unnamed Room'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>

              {/* Product Selection */}
              <div className="p-4 border rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Product Selection</Label>
                  {(() => {
                    // Check if this is a Portal.io imported product
                    const isPortalProduct = selectedFixture.category_info?.from_portal === true;

                    const button = (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowProductModal(true)}
                        disabled={isPortalProduct || isUpdating}
                      >
                        {linkedProduct ? 'Change Product' : 'Select Product'}
                      </Button>
                    );

                    if (isPortalProduct) {
                      return (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>{button}</TooltipTrigger>
                            <TooltipContent>
                              <p>Product is linked to Portal.io proposal</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    }

                    return button;
                  })()}
                </div>

                {linkedProduct && (
                  <div className="p-4 bg-muted rounded-lg space-y-3">
                    {/* Product Image */}
                    {(() => {
                      // Try to get image URL from linked product or Portal metadata
                      const imageUrl =
                        linkedProduct?.image_url ||
                        (linkedProduct?.portal_metadata as any)?.imageUrl ||
                        (selectedFixture?.category_info as any)?.portal_image_url;

                      if (!imageUrl) return null;

                      return (
                        <div className="relative aspect-square w-24 h-24 mx-auto overflow-hidden rounded-lg border bg-muted">
                          <img
                            src={imageUrl}
                            alt={`${linkedProduct?.brand || selectedFixture?.custom_name} ${linkedProduct?.model || ''}`}
                            className="h-full w-full object-contain p-1.5"
                            onError={e => {
                              // Hide image container if image fails to load
                              const container = e.currentTarget.parentElement;
                              if (container) {
                                container.style.display = 'none';
                              }
                            }}
                          />
                        </div>
                      );
                    })()}

                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="font-medium">{linkedProduct.brand}</p>
                        <p className="text-sm text-muted-foreground">{linkedProduct.model}</p>
                        {linkedProduct.part_number && (
                          <p className="text-xs font-mono text-muted-foreground">
                            {linkedProduct.part_number}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleUnassignProduct}
                        disabled={isUpdating}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    {linkedProduct.description && (
                      <p className="text-sm text-muted-foreground">{linkedProduct.description}</p>
                    )}

                    {/* Specifications - Fixture Specific */}
                    {linkedProduct.specifications && (
                      <div className="border-t pt-2 text-xs text-muted-foreground space-y-1">
                        {linkedProduct.specifications.lumens && (
                          <div>Lumens: {linkedProduct.specifications.lumens}</div>
                        )}
                        {linkedProduct.specifications.beam_angle && (
                          <div>Beam Angle: {linkedProduct.specifications.beam_angle}°</div>
                        )}
                        {linkedProduct.specifications.color_temperature && (
                          <div>Color Temp: {linkedProduct.specifications.color_temperature}K</div>
                        )}
                      </div>
                    )}

                    {/* Category Icon Section - Edit icon for ALL fixtures of this category/subcategory */}
                    <Separator className="my-3" />
                    {(() => {
                      // Determine what we're editing: subcategory (if exists) or category
                      const hasSubcategory = !!selectedFixture?.subcategory;

                      // Find the target category from merged categories
                      const targetCategory = hasSubcategory
                        ? lightingTemplates.find(
                            c =>
                              c.category === selectedFixture?.category &&
                              c.subcategory === selectedFixture?.subcategory,
                          )
                        : lightingTemplates.find(
                            c => c.category === selectedFixture?.category && !c.subcategory,
                          );

                      // Count affected fixtures
                      const affectedCount = (data.fixtures || []).filter(f => {
                        if (hasSubcategory) {
                          return (
                            f.category === selectedFixture?.category &&
                            f.subcategory === selectedFixture?.subcategory
                          );
                        }
                        return f.category === selectedFixture?.category;
                      }).length;

                      // Display name for the icon
                      const iconLabel =
                        targetCategory?.display_name ||
                        (hasSubcategory
                          ? selectedFixture?.subcategory!.replace(/_/g, ' ').replace(/-/g, ' ')
                          : selectedFixture?.category.replace(/_/g, ' ').replace(/-/g, ' '));

                      return (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs text-muted-foreground">Category Icon</Label>
                            <Badge variant="outline" className="text-xs">
                              {affectedCount} fixture{affectedCount !== 1 ? 's' : ''}
                            </Badge>
                          </div>
                          <IconSelector
                            currentIcon={{
                              iconType: targetCategory?.icon_svg
                                ? 'svg'
                                : targetCategory?.abbreviation
                                  ? 'abbreviation'
                                  : 'none',
                              iconSvg: targetCategory?.icon_svg,
                              abbreviation: targetCategory?.abbreviation,
                              fillColor: targetCategory?.fill_color,
                              borderColor: targetCategory?.border_color,
                              textColor: targetCategory?.text_color,
                              iconShape: targetCategory?.icon_shape,
                            }}
                            categoryIcon={{
                              icon_type: 'abbreviation' as const,
                              abbreviation: (hasSubcategory
                                ? selectedFixture?.subcategory || 'FIX'
                                : selectedFixture?.category || 'FIX'
                              )
                                .slice(0, 3)
                                .toUpperCase(),
                              fill_color: targetCategory?.fill_color || '#6B7280',
                              border_color: targetCategory?.border_color || '#374151',
                              text_color: targetCategory?.text_color || '#FFFFFF',
                            }}
                            productName={iconLabel || 'Fixture'}
                            showResetButton={!!targetCategory?.org_category_id}
                            onSave={async (iconData: IconData) => {
                              setIsSavingIcon(true);
                              try {
                                // Handle reset to default (iconType === 'none')
                                if (iconData.iconType === 'none') {
                                  if (targetCategory?.org_category_id) {
                                    await organizationCategoryService.resetIconToDefault(
                                      targetCategory.org_category_id,
                                    );
                                    toast.success(
                                      `Reset icon for all ${iconLabel} fixtures to default`,
                                    );
                                  } else {
                                    toast.info('Icon is already using the default');
                                  }
                                } else if (targetCategory?.org_category_id) {
                                  // Update existing org category
                                  await organizationCategoryService.updateCategory(
                                    targetCategory.org_category_id,
                                    {
                                      icon_type:
                                        iconData.iconType === 'svg' ? 'svg' : 'abbreviation',
                                      icon_svg:
                                        iconData.iconType === 'svg' ? iconData.iconSvg : null,
                                      abbreviation:
                                        iconData.iconType === 'abbreviation'
                                          ? iconData.abbreviation
                                          : null,
                                      fill_color: iconData.fillColor || '#6B7280',
                                      border_color: iconData.borderColor || '#374151',
                                      text_color: iconData.textColor || '#FFFFFF',
                                      icon_shape: iconData.iconShape || 'square',
                                    },
                                  );
                                  toast.success(`Updated icon for all ${iconLabel} fixtures`);
                                } else {
                                  // Create new org category to override system template
                                  await organizationCategoryService.createCategory({
                                    domain: 'lighting_fixtures',
                                    category: selectedFixture?.category || '',
                                    subcategory: hasSubcategory
                                      ? selectedFixture?.subcategory
                                      : null,
                                    display_name: iconLabel || '',
                                    icon_type: iconData.iconType === 'svg' ? 'svg' : 'abbreviation',
                                    icon_svg: iconData.iconType === 'svg' ? iconData.iconSvg : null,
                                    abbreviation:
                                      iconData.iconType === 'abbreviation'
                                        ? iconData.abbreviation
                                        : null,
                                    fill_color: iconData.fillColor || '#6B7280',
                                    border_color: iconData.borderColor || '#374151',
                                    text_color: iconData.textColor || '#FFFFFF',
                                    icon_shape: iconData.iconShape || 'square',
                                  });
                                  toast.success(`Set icon for all ${iconLabel} fixtures`);
                                }

                                // Invalidate all relevant caches
                                queryClient.invalidateQueries({ queryKey: ['merged-categories'] });
                                queryClient.invalidateQueries({
                                  queryKey: ['organization-categories'],
                                });
                                queryClient.invalidateQueries({ queryKey: ['fixtures'] });

                                // Trigger icon re-fetch in all components
                                incrementCategoryIconVersion();
                              } catch (error) {
                                logger.error('Failed to update icon:', error);
                                toast.error('Failed to update icon');
                              } finally {
                                setIsSavingIcon(false);
                              }
                            }}
                            isSaving={isSavingIcon}
                            previewSize={24}
                          />
                          <p className="text-xs text-muted-foreground">
                            Applies to all {iconLabel} fixtures on floor plans.
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {!linkedProduct && (
                  <div className="p-2 bg-muted/50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">
                      No product selected. This is a generic{' '}
                      {selectedFixture.fixture_type.replace(/_/g, ' ')}.
                    </p>
                  </div>
                )}

                {/* Show Product Image Toggle - only show if product has image */}
                {linkedProduct &&
                  (() => {
                    const imageUrl =
                      linkedProduct?.image_url ||
                      (linkedProduct?.portal_metadata as any)?.imageUrl ||
                      (selectedFixture?.category_info as any)?.portal_image_url;
                    if (!imageUrl) return null;
                    return (
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-2">
                          <ImageIcon className="h-4 w-4 text-muted-foreground" />
                          <Label
                            htmlFor="show-product-image"
                            className="cursor-pointer text-sm font-normal"
                          >
                            Show image on floor plan
                          </Label>
                        </div>
                        <Switch
                          id="show-product-image"
                          checked={selectedFixture.show_product_image || false}
                          onCheckedChange={async checked => {
                            try {
                              setIsUpdating(true);
                              await updateFixture(selectedFixture.id, {
                                show_product_image: checked,
                              });
                              await data.refetch();
                              toast.success(
                                checked
                                  ? 'Product image shown on floor plan'
                                  : 'Product image hidden',
                              );
                            } catch (error) {
                              logger.error('Error updating show product image:', error);
                              toast.error('Failed to update setting');
                            } finally {
                              setIsUpdating(false);
                            }
                          }}
                          disabled={isUpdating}
                        />
                      </div>
                    );
                  })()}
              </div>

              {/* Load Assignment */}
              <div className="p-4 border rounded-lg space-y-4">
                <h4 className="text-sm font-medium">Load Assignment</h4>

                {/* Current Load */}
                {fixtureCircuit ? (
                  <div className="space-y-3">
                    <div>
                      <Label>Current Load</Label>
                      {isEditingCircuit ? (
                        <div className="mt-2 flex gap-2">
                          <Input
                            ref={circuitNameInputRef}
                            value={newCircuitName}
                            onChange={e => setNewCircuitName(e.target.value)}
                            onKeyDown={handleCircuitNameKeyDown}
                            onKeyUp={e => e.stopPropagation()}
                            className="flex-1"
                            placeholder="Load name"
                            disabled={isUpdating}
                          />
                          <Button
                            size="sm"
                            onClick={saveCircuitName}
                            disabled={!newCircuitName.trim() || isUpdating}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEditCircuit}
                            disabled={isUpdating}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="mt-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge>
                              {fixtureCircuit.sequence_number
                                ? `${fixtureCircuit.sequence_number} - `
                                : ''}
                              {fixtureCircuit.name}
                            </Badge>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={startEditCircuit}
                            disabled={isUpdating}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAssignToCircuit(null)}
                      disabled={isUpdating}
                      className="w-full"
                    >
                      <Unplug className="h-4 w-4 mr-2" />
                      Remove from Load
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">Not assigned to any load</div>
                )}

                {/* Load Selection */}
                <div>
                  <Label>Assign to Load</Label>
                  <Select onValueChange={handleAssignToCircuit} disabled={isUpdating}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select a load" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* None option */}
                      <SelectItem value="none">None</SelectItem>

                      {/* Currently Assigned section */}
                      {fixtureCircuit && (
                        <div>
                          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground bg-muted/50">
                            Currently Assigned
                          </div>
                          <SelectItem
                            key={fixtureCircuit.id}
                            value={fixtureCircuit.id}
                            onMouseEnter={() => setHoveredEntity(fixtureCircuit.id, 'circuit')}
                            onMouseLeave={() => setHoveredEntity(null, null)}
                          >
                            {fixtureCircuit.sequence_number
                              ? `${fixtureCircuit.sequence_number} - `
                              : ''}
                            {fixtureCircuit.name}
                          </SelectItem>
                        </div>
                      )}

                      {/* Current Floor Plan section */}
                      {availableCircuits.length > 0 && (
                        <div>
                          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground bg-muted/50">
                            Current Floor Plan
                          </div>
                          {availableCircuits.map(circuit => (
                            <SelectItem
                              key={circuit.id}
                              value={circuit.id}
                              onMouseEnter={() => setHoveredEntity(circuit.id, 'circuit')}
                              onMouseLeave={() => setHoveredEntity(null, null)}
                            >
                              {circuit.sequence_number ? `${circuit.sequence_number} - ` : ''}
                              {circuit.name}
                            </SelectItem>
                          ))}
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Resize Control - Shows "Thickness" for LED strips, "Scale" for other fixtures */}
              <div className="p-4 border rounded-lg space-y-4">
                <h4 className="text-sm font-medium">
                  {selectedFixture.fixture_form_factor === 'linear' ? 'Line Thickness' : 'Scale'}
                </h4>
                <ResizeControl
                  entityType="fixture"
                  entity={selectedFixture}
                  allEntities={data.fixtures || []}
                  onResize={handleFixtureResize}
                  isUpdating={isUpdating}
                  baseScale={fixturesBaseScale}
                  label={selectedFixture.fixture_form_factor === 'linear' ? 'Thickness' : 'Scale'}
                />
                {selectedFixture.fixture_form_factor === 'linear' && (
                  <p className="text-xs text-muted-foreground">
                    Adjusts the line thickness of the LED strip
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="p-4 border rounded-lg space-y-3">
                <h4 className="text-sm font-medium">Actions</h4>

                <div className="grid gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCloneFixture}
                    disabled={isUpdating}
                    className="w-full"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Clone Fixture
                  </Button>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={isUpdating} className="w-full">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Fixture
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Fixture</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone. This will permanently delete this{' '}
                          {selectedFixture.fixture_type.replace('_', ' ')} fixture
                          {fixtureCircuit && ` from load "${fixtureCircuit.name}"`}.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteFixture}
                          className="bg-destructive hover:bg-destructive/90"
                        >
                          Delete Fixture
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Product Selection Modal */}
        {selectedFixture && (
          <ProductSelectionModal
            open={showProductModal}
            onOpenChange={setShowProductModal}
            currentProductId={selectedFixture.product_id}
            category="fixture"
            subcategory={selectedFixture.subcategory}
            title="Select Lighting Product"
            onSelect={async product => {
              try {
                setIsUpdating(true);

                // Build update object
                const updateData: any = {
                  product_id: product?.id || null,
                  // If a product is selected, update wattage from product specs
                  wattage: product?.wattage || selectedFixture.wattage,
                };

                // If unassigning a Portal product, clear Portal metadata
                if (!product && selectedFixture.category_info?.from_portal) {
                  updateData.category_info = {
                    ...selectedFixture.category_info,
                    portal_product_id: null,
                    from_portal: false,
                  };
                }

                // Update fixture with product_id and cleared Portal metadata (if unassigning)
                await updateFixture(selectedFixture.id, updateData);

                await data.refetch();

                // Update temp wattage if product has wattage
                if (product?.wattage) {
                  setTempWattage(product.wattage.toString());
                }

                toast.success(
                  product ? `Linked to ${product.brand} ${product.model}` : 'Product link removed',
                );

                setShowProductModal(false);
              } catch (error) {
                logger.error('Error updating product link:', error);
                toast.error('Failed to update product link');
              } finally {
                setIsUpdating(false);
              }
            }}
          />
        )}
      </>
    );
  }

  return null;
}
